import React from "react";
import { Box, Typography, Paper, Divider } from "@mui/material";
import { NodeType, NODE_METADATA } from "./types";

interface NodePaletteProps {
  onDragStart: (event: React.DragEvent, nodeType: NodeType) => void;
}

/**
 * Node palette showing all available node types for drag-and-drop
 */
const NodePalette: React.FC<NodePaletteProps> = ({ onDragStart }) => {
  const controlNodes = Object.values(NODE_METADATA).filter(
    (meta) => meta.category === "control"
  );
  const actionNodes = Object.values(NODE_METADATA).filter((meta) => meta.category === "action");

  return (
    <Box sx={{ width: 260, height: "100%", overflow: "auto", p: 2, backgroundColor: "#F8F9FA" }}>
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom sx={{ fontSize: "1rem" }}>
          Add Nodes
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.3, fontSize: "0.7rem" }}>
          Drag nodes to canvas to build conversation flow
        </Typography>
      </Box>

      {/* Control Flow Nodes */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ color: "#3B82F6", mb: 1, fontSize: "0.8rem" }}>
          Control Flow
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {controlNodes.map((meta) => (
            <PaletteNode key={meta.type} meta={meta} onDragStart={onDragStart} />
          ))}
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Action Nodes */}
      <Box>
        <Typography variant="subtitle2" fontWeight={600} sx={{ color: "#EC4899", mb: 1, fontSize: "0.8rem" }}>
          Actions
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {actionNodes.map((meta) => (
            <PaletteNode key={meta.type} meta={meta} onDragStart={onDragStart} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

interface PaletteNodeProps {
  meta: typeof NODE_METADATA[NodeType];
  onDragStart: (event: React.DragEvent, nodeType: NodeType) => void;
}

const PaletteNode: React.FC<PaletteNodeProps> = ({ meta, onDragStart }) => {
  return (
    <Paper
      draggable
      onDragStart={(e) => onDragStart(e, meta.type)}
      elevation={0}
      sx={{
        p: 1,
        cursor: "grab",
        border: `1.5px solid ${meta.color}30`,
        borderLeftWidth: 3,
        borderLeftColor: meta.color,
        backgroundColor: "white",
        transition: "all 0.15s",
        "&:hover": {
          transform: "translateX(3px)",
          boxShadow: 1,
          backgroundColor: `${meta.color}05`,
          borderColor: `${meta.color}50`,
        },
        "&:active": {
          cursor: "grabbing",
          transform: "translateX(1px)",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Typography sx={{ fontSize: "1.2rem", lineHeight: 1 }}>{meta.icon}</Typography>
        <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.75rem" }}>
          {meta.label}
        </Typography>
      </Box>
    </Paper>
  );
};

export default NodePalette;
